import React, { lazy, Suspense, useMemo } from "react"
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom"

import { ifAnyGranted } from "./auth"
import Checking from "./dashboard/checking"
import OrderIntegration from "./dashboard/orderIntegration"
import PaymentExclusion from "./dashboard/paymentExclusion"
import Financial from "./dashboard/financial"
import Franchise from "./dashboard/franchise"
import Product from "./dashboard/product"
import RealTime from "./dashboard/realTime"
import Sales from "./dashboard/sales"
import Unauthorized from "./dashboard/unauthorized"
import { Layout } from "./layout"
import { AuthorizedDefaultLayout } from "./layout/AuthorizedDefaultLayout"
import Login from "./login"
import AuthService from "./service"
import Target from "./target"
import PerfomanceOperation from "./dashboard/performanceOperation"
import Customer from "./dashboard/customer"
import { BodyWrapper } from './component/generic/iframes/BodyWrapper'
import { IframeDialogsWrapper } from "./component/generic/iframes/IframeDialogsWrapper"
import AdminCache from "./dashboard/adminCache"
import Report from "./report"

function isAuthenticated() {
    return AuthService.loggedIn()
}

function isAuthorized(role) {
    if (role) {
        return ifAnyGranted(role)
    }

    return true
}

function RequireAuth({ children }) {
    const location = useLocation()

    if (!isAuthenticated()) {
        return <Navigate to="/login" state={{ from: location }} replace />
    }

    return children
}

function RequireRole({ children, role }) {
    if (!isAuthorized(role)) {
        return <Navigate to={`/unauthorized/${role}`} />
    }

    return children
}

export function IframeDynamicComponent() {
    const [searchParams] = useSearchParams()
    const type = searchParams.get('type')
    const componentsPath = searchParams.get('urls') || ''

    const components = useMemo(() => {
        const componentsPathArr = componentsPath.split(',')

        return componentsPathArr
            .filter(item => item.trim())
            .map(item => lazy(() => import(`./gerencial/${item.trim()}`)))
    }, [componentsPath])

    if (!components || !components.length) {
        return null
    }

    if (type === 'main') {
        return (
            <Suspense fallback={<div>Carregando...</div>}>
                <BodyWrapper>
                    {components.map((Component, index) => <Component key={index} />)}
                </BodyWrapper>
            </Suspense>
        )
    }

    return (
        <Suspense fallback={<div>Carregando...</div>}>
            <IframeDialogsWrapper>
                {components.map((Component, index) => <Component key={index} />)}
            </IframeDialogsWrapper>
        </Suspense>
    )
}

function Router() {
    return (
        <BrowserRouter>
            <Routes>
                <Route exact path="/login" element={<Login />} />
                <Route path="/gerencial/:jwt" element={<IframeDynamicComponent />} />
                <Route element={<RequireAuth><Layout /></RequireAuth>}>
                    <Route element={<AuthorizedDefaultLayout />}>
                        <Route path="/" element={<RealTime />} />
                        <Route path="/sales" element={<Sales />} />
                        <Route path="/financial" element={<Financial />} />
                        <Route path="/product" element={<Product />} />
                        <Route path="/customer" element={<Customer />} />
                        <Route path="/checking" element={<Checking />} />
                        <Route path="/orderIntegration" element={<OrderIntegration />} />
                        <Route path="/paymentExclusion" element={<PaymentExclusion />} />
                        <Route path="/franchise" element={(
                            <RequireRole role="ROLE_SISCHEF_ANALYTICS_PRO">
                                <Franchise />
                            </RequireRole>
                        )} />
                        <Route path="/performanceOperation" element={(
                            <RequireRole role="ROLE_SISCHEF_ANALYTICS_PRO">
                                <PerfomanceOperation />
                            </RequireRole>
                        )} />
                        <Route path="/target" element={<Target />} />
                    </Route>
                    {/* Fora do AuthorizedDefaultLayout: a tela tem filtro e exportação próprios */}
                    <Route path="/reports" element={<Report />} />
                    <Route path="/admin/cache" element={(
                        <RequireRole role="ROLE_ADMIN">
                            <AdminCache />
                        </RequireRole>
                    )} />
                    <Route path="/unauthorized/:role" element={<Unauthorized />} />
                </Route>
            </Routes>
        </BrowserRouter>
    )
}

export default Router